import { useState } from 'react';
import { FormData } from '../types';

interface Props {
  data: Pick<FormData, 'nombre' | 'apellido' | 'email' | 'telefono' | 'unidad' | 'orcid' | 'postgrado'>;
  onChange: (field: keyof FormData, value: string) => void;
  errors: Partial<Record<keyof FormData, string>>;
  emailReadOnly?: boolean;
}

const POSTGRADO_TIPOS = [
  { value: 'Doctorado', icon: 'ri-medal-2-line', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-300' },
  { value: 'Maestría', icon: 'ri-award-line', color: 'text-[#0D3157]', bg: 'bg-blue-50', border: 'border-blue-300' },
  { value: 'Especialidad', icon: 'ri-stethoscope-line', color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-300' },
  { value: 'Diplomado', icon: 'ri-file-text-line', color: 'text-violet-700', bg: 'bg-violet-50', border: 'border-violet-300' },
];

function parsePostgrado(value: string): { tipo: string; detalle: string } {
  for (const t of POSTGRADO_TIPOS) {
    if (value.startsWith(t.value)) {
      const rest = value.slice(t.value.length).replace(/^[\s:–-]+/, '');
      return { tipo: t.value, detalle: rest };
    }
  }
  return { tipo: '', detalle: value };
}

export default function DatosPersonalesSection({ data, onChange, errors, emailReadOnly = false }: Props) {
  const parsed = parsePostgrado(data.postgrado ?? '');
  const [tipoSeleccionado, setTipoSeleccionado] = useState<string>(parsed.tipo);
  const [detallePostgrado, setDetallePostgrado] = useState<string>(parsed.detalle);

  const handleTipoClick = (tipo: string) => {
    const nuevoTipo = tipoSeleccionado === tipo ? '' : tipo;
    setTipoSeleccionado(nuevoTipo);
    const combined = nuevoTipo ? (detallePostgrado ? `${nuevoTipo} en ${detallePostgrado}` : nuevoTipo) : detallePostgrado;
    onChange('postgrado', combined);
  };

  const handleDetalleChange = (val: string) => {
    setDetallePostgrado(val);
    const combined = tipoSeleccionado ? (val ? `${tipoSeleccionado} en ${val}` : tipoSeleccionado) : val;
    onChange('postgrado', combined);
  };

  return (
    <section id="datos-personales" className="max-w-5xl mx-auto px-6 mt-8">
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="bg-slate-50 border-b border-slate-200 px-6 py-4 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-[#0D3157] rounded-lg">
            <i className="ri-user-3-line text-white text-sm" />
          </div>
          <div>
            <h2 className="text-[#0D3157] font-bold text-base">
              Datos Personales
            </h2>
            <p className="text-slate-400 text-xs">Información de identificación del investigador</p>
          </div>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
              Nombre <span className="text-amber-500">*</span>
            </label>
            <input
              type="text"
              name="nombre"
              value={data.nombre}
              onChange={(e) => onChange('nombre', e.target.value)}
              placeholder="Ingrese su nombre"
              className={`w-full border rounded-lg px-3 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0D3157]/30 focus:border-[#0D3157] transition-colors ${errors.nombre ? 'border-red-400 bg-red-50' : 'border-slate-300'}`}
            />
            {errors.nombre && (
              <p className="text-red-500 text-xs mt-1">{errors.nombre}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
              Apellido <span className="text-amber-500">*</span>
            </label>
            <input
              type="text"
              name="apellido"
              value={data.apellido}
              onChange={(e) => onChange('apellido', e.target.value)}
              placeholder="Ingrese su apellido"
              className={`w-full border rounded-lg px-3 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0D3157]/30 focus:border-[#0D3157] transition-colors ${errors.apellido ? 'border-red-400 bg-red-50' : 'border-slate-300'}`}
            />
            {errors.apellido && (
              <p className="text-red-500 text-xs mt-1">{errors.apellido}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
              Correo Electrónico <span className="text-amber-500">*</span>
            </label>
            <div className="relative">
              <input
                type="email"
                name="email"
                value={data.email}
                onChange={(e) => !emailReadOnly && onChange('email', e.target.value)}
                readOnly={emailReadOnly}
                placeholder="correo@upal.edu.bo"
                className={`w-full border rounded-lg px-3 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none transition-colors ${
                  emailReadOnly
                    ? 'bg-slate-100 text-slate-500 cursor-not-allowed border-slate-200'
                    : errors.email
                    ? 'border-red-400 bg-red-50 focus:ring-2 focus:ring-[#0D3157]/30 focus:border-[#0D3157]'
                    : 'border-slate-300 focus:ring-2 focus:ring-[#0D3157]/30 focus:border-[#0D3157]'
                }`}
              />
              {emailReadOnly && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-lock-2-line text-slate-400 text-xs" />
                </div>
              )}
            </div>
            {emailReadOnly && (
              <p className="text-xs text-slate-400 mt-1">
                Tomado de su cuenta activa
              </p>
            )}
            {errors.email && !emailReadOnly && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
              Teléfono / Celular
            </label>
            <input
              type="tel"
              name="telefono"
              value={data.telefono}
              onChange={(e) => onChange('telefono', e.target.value)}
              placeholder="+591 7XXXXXXX"
              className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0D3157]/30 focus:border-[#0D3157] transition-colors"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
              Profesión <span className="text-amber-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                <i className="ri-briefcase-4-line text-slate-400 text-sm" />
              </div>
              <input
                type="text"
                name="unidad"
                value={data.unidad}
                onChange={(e) => onChange('unidad', e.target.value)}
                placeholder="Escriba su profesión o carrera de formación"
                className={`w-full border rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0D3157]/30 focus:border-[#0D3157] transition-colors ${errors.unidad ? 'border-red-400 bg-red-50' : 'border-slate-300'}`}
              />
            </div>
            <div className="mt-1.5 space-y-0.5">
              <p className="text-xs text-amber-600 font-medium">
                <i className="ri-information-line mr-1" />
                Primera letra de cada palabra en mayúscula, el resto en minúscula. Sin abreviaciones.
              </p>
              <p className="text-xs text-slate-400">
                Ejemplo: <span className="font-medium text-slate-500">Medicina</span> &nbsp;·&nbsp; <span className="font-medium text-slate-500">Ingeniería de Sistemas</span> &nbsp;·&nbsp; <span className="font-medium text-slate-500">Derecho</span>
              </p>
            </div>
            {errors.unidad && (
              <p className="text-red-500 text-xs mt-1">{errors.unidad}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
              Postgrado
              <span className="ml-1.5 text-slate-400 font-normal normal-case tracking-normal text-xs">(opcional)</span>
            </label>

            {/* Etiquetas de tipo de postgrado */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
              {POSTGRADO_TIPOS.map((tipo) => {
                const isActive = tipoSeleccionado === tipo.value;
                return (
                  <button
                    key={tipo.value}
                    type="button"
                    onClick={() => handleTipoClick(tipo.value)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 cursor-pointer transition-all whitespace-nowrap ${
                      isActive
                        ? `${tipo.border} ${tipo.bg}`
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <div className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 ${isActive ? tipo.bg : 'bg-slate-100'}`}>
                      <i className={`${tipo.icon} text-xs ${isActive ? tipo.color : 'text-slate-400'}`} />
                    </div>
                    <span className={`text-xs font-semibold ${isActive ? tipo.color : 'text-slate-500'}`}>
                      {tipo.value}
                    </span>
                    {isActive && (
                      <div className={`ml-auto w-4 h-4 flex items-center justify-center rounded-full flex-shrink-0 ${tipo.bg} border ${tipo.border}`}>
                        <i className={`ri-check-line text-[10px] ${tipo.color}`} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Campo de detalle (nombre específico del postgrado) */}
            {tipoSeleccionado && (
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                  <i className="ri-pencil-line text-slate-400 text-sm" />
                </div>
                <input
                  type="text"
                  value={detallePostgrado}
                  onChange={(e) => handleDetalleChange(e.target.value)}
                  placeholder={`Nombre del ${tipoSeleccionado} (ej: ${tipoSeleccionado} en Medicina)`}
                  className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0D3157]/30 focus:border-[#0D3157] transition-colors"
                />
                <div className="mt-1.5 space-y-0.5">
                  <p className="text-xs text-amber-600 font-medium">
                    <i className="ri-information-line mr-1" />
                    Primera letra de cada palabra en mayúscula, el resto en minúscula. Sin abreviaciones.
                  </p>
                  <p className="text-xs text-slate-400">
                    Se guardará como: <span className="font-medium text-slate-600">{tipoSeleccionado}{detallePostgrado ? ` en ${detallePostgrado}` : ''}</span>
                  </p>
                </div>
              </div>
            )}

            {!tipoSeleccionado && (
              <p className="text-xs text-slate-400">
                <i className="ri-arrow-up-line mr-1" />
                Seleccione el tipo de postgrado para continuar
              </p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
              Número ORCID <span className="text-slate-400 font-normal normal-case">(URL)</span>
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                <i className="ri-id-card-line text-slate-400 text-sm" />
              </div>
              <input
                type="url"
                name="orcid"
                value={data.orcid}
                onChange={(e) => onChange('orcid', e.target.value)}
                placeholder="https://orcid.org/0000-0000-0000-0000"
                className={`w-full border rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0D3157]/30 focus:border-[#0D3157] transition-colors ${errors.orcid ? 'border-red-400 bg-red-50' : 'border-slate-300'}`}
              />
            </div>
            <p className="text-xs text-slate-400 mt-1">
              Ingrese la URL completa de su perfil ORCID. Ejemplo: <span className="font-mono text-slate-500">https://orcid.org/0000-0001-8702-3405</span>
            </p>
            {errors.orcid && (
              <p className="text-red-500 text-xs mt-1">{errors.orcid}</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
